import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from "@material-ui/core";
import CkSelect from "./CkSelect";
import useCheckoutClassicStyles from "./checkoutClassicStyles";

// Mirrors the reference RegionalPlansPage form column: a label
// (formFieldLabel) directly followed by its control, with no per-field wrapper.
// Text fields carry their own bottom margin via the copied formField class and
// surface errors through MUI's error/helperText (exactly like the reference).
// The phone row and custom select don't carry that margin, so they get a 12px
// gap wrapper (fieldBlock) to keep the vertical rhythm uniform.
export default function LeadForm({
  formTitle,
  formValues,
  setField,
  onFieldBlur,
  fieldErrors,
  formError,
  configFields,
  phoneField,
  renderCountrySelect,
  submitting,
  onContinue,
  ctaLabel,
  hideCta,
  onDropdownOpenChange,
}) {
  const classes = useCheckoutClassicStyles();

  const label = (text) => (
    <Typography variant="body2" className={classes.formFieldLabel}>
      {text}
    </Typography>
  );

  const textField = (key, placeholder) => (
    <TextField
      data-field={key}
      className={classes.formField}
      fullWidth
      variant="outlined"
      placeholder={placeholder}
      value={formValues[key] || ""}
      onChange={(e) => setField(key, e.target.value)}
      onBlur={() => onFieldBlur && onFieldBlur(key)}
      error={!!fieldErrors[key]}
      helperText={fieldErrors[key] || ""}
    />
  );

  // Single unified field, mirroring the reference: one outlined formField
  // TextField whose startAdornment is the tinted, divider-separated country
  // picker (countryCodeInput) — not two separate boxes.
  const phoneBlock = (field, key, placeholder) => (
    <Box className={classes.phoneInputContainer} data-field={key}>
      <TextField
        className={classes.formField}
        fullWidth
        variant="outlined"
        type="tel"
        placeholder={placeholder}
        value={formValues[key] || ""}
        onChange={(e) => setField(key, e.target.value.replace(/[^0-9]/g, ""))}
        onBlur={() => onFieldBlur && onFieldBlur(key)}
        error={!!fieldErrors[key]}
        helperText={fieldErrors[key] || ""}
        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" style={{ marginRight: 0 }}>
              <Box className={classes.countryCodeInput}>
                {renderCountrySelect(field, key)}
              </Box>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );

  const renderConfigField = (f) => {
    const lbl = `${f.label}${f.required ? "*" : ""}`;
    if (f.type === "select") {
      return (
        <Box key={f.key} className={classes.fieldBlock} data-field={f.key}>
          {label(lbl)}
          <CkSelect
            ariaLabel={f.label}
            value={formValues[f.key] || ""}
            placeholder={
              f.placeholder || `Select ${String(f.label || "").toLowerCase()}`
            }
            options={(f.options || []).map((o) => ({
              value: o.value,
              label: o.label || o.value,
            }))}
            onChange={(v) => setField(f.key, v)}
            onOpenChange={
              onDropdownOpenChange
                ? (isOpen) => onDropdownOpenChange(`field:${f.key}`, isOpen)
                : undefined
            }
          />
          {fieldErrors[f.key] && (
            <div className={classes.fieldError}>{fieldErrors[f.key]}</div>
          )}
        </Box>
      );
    }
    if (f.type === "phone") {
      return (
        <React.Fragment key={f.key}>
          {label(lbl)}
          {phoneBlock(f, f.key, f.placeholder || "Enter phone number")}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={f.key}>
        {label(lbl)}
        {textField(f.key, f.placeholder || f.label)}
      </React.Fragment>
    );
  };

  return (
    <Box>
      {/* Hidden on mobile, — the hero heading is enough. */}
      <Typography
        variant="h5"
        className={`${classes.formTitle} ${classes.hideOnMobile}`}
      >
        {formTitle}
      </Typography>

      {label("Full Name*")}
      {textField("fullName", "Enter your full name")}

      {label("Email Address*")}
      {textField("emailAddress", "Enter your email address")}

      {label("Phone Number*")}
      {phoneBlock(phoneField, "phoneNumber", "Enter your phone number")}

      {configFields.map(renderConfigField)}

      {formError && (
        <div className={classes.fieldError} data-error-anchor>
          {formError}
        </div>
      )}

      {/* Replaced by MobileStickyBar on mobile. */}
      <Button
        className={
          hideCta
            ? `${classes.proceedButton} ${classes.hideOnMobile}`
            : classes.proceedButton
        }
        fullWidth
        variant="contained"
        onClick={onContinue}
        disabled={submitting}
      >
        {submitting ? "Processing…" : ctaLabel}
      </Button>
    </Box>
  );
}
